import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import api from '../services/api';
import { CalendarClock, AlertCircle, CheckCircle2, Flag, RefreshCw, TrendingUp, Flame, Clock } from 'lucide-react';

interface UpcomingPayment {
    id: string;
    title: string;
    type: string;
    amount: number;
    dueDate: string;
    startDate?: string;
    status: string;
    currency: string;
}

const UpcomingPayments: React.FC = () => {
    const { currency } = useAuth();
    const [payments, setPayments] = useState<UpcomingPayment[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'client' | 'developer'>('client');
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        const fetchUpcoming = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/dashboard/upcoming-payments?filter=${filter}`);
                setPayments(res.data);
            } catch (err) {
                console.error('Failed to fetch upcoming payments', err);
            } finally {
                setLoading(false);
            }
        };
        fetchUpcoming();
    }, [filter]);

    const isDueSoon = (dueDate: string) => {
        if (!dueDate) return false;
        const diff = Math.ceil((new Date(dueDate).getTime() - Date.now()) / 86400000);
        return diff <= 7 && diff >= 0;
    };

    const isOverdue = (dueDate: string, status: string) => {
        if (!dueDate) return false;
        if (status === 'Overdue') return true;
        return new Date(dueDate) < new Date() && status !== 'Paid';
    };

    const getDaysLabel = (dueDate: string, status: string) => {
        if (!dueDate) return null;
        const diff = Math.ceil((new Date(dueDate).getTime() - Date.now()) / 86400000);
        if (isOverdue(dueDate, status)) return { label: `${Math.abs(diff)}d overdue`, color: 'text-red-600 bg-red-100' };
        if (diff === 0) return { label: 'Today', color: 'text-orange-600 bg-orange-100' };
        if (diff <= 3) return { label: `${diff}d left`, color: 'text-orange-600 bg-orange-100' };
        if (diff <= 7) return { label: `${diff}d left`, color: 'text-yellow-700 bg-yellow-100' };
        return { label: `${diff}d`, color: 'text-gray-500 bg-gray-100' };
    };

    if (loading) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                    {[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded-lg"></div>)}
                </div>
            </div>
        );
    }

    const clientPayments = payments.filter(p => ['Invoice', 'Project Installment', 'Project Deadline', 'Software', 'Hosting'].includes(p.type));
    const devPayments = payments.filter(p => p.type === 'Developer Payout');
    const displayPayments = activeTab === 'client' ? clientPayments : devPayments;

    const overdueCount = displayPayments.filter(p => isOverdue(p.dueDate, p.status)).length;
    const dueSoonCount = displayPayments.filter(p => isDueSoon(p.dueDate) && !isOverdue(p.dueDate, p.status)).length;
    const totalDue = displayPayments.filter(p => p.type !== 'Project Deadline').reduce((s, p) => s + (p.amount || 0), 0);

    const motivMsg = overdueCount > 0
        ? `${overdueCount} overdue — act now!`
        : dueSoonCount > 0
            ? `${dueSoonCount} due this week — stay on top!`
            : displayPayments.length === 0
                ? "All clear — great work!"
                : "Everything on track!";

    const motivColor = overdueCount > 0 ? 'from-red-500 to-orange-500' : dueSoonCount > 0 ? 'from-orange-400 to-yellow-500' : 'from-emerald-500 to-teal-500';
    const MotivIcon = overdueCount > 0 ? Flame : dueSoonCount > 0 ? Clock : TrendingUp;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Motivational mini-banner */}
            <div className={`bg-gradient-to-r ${motivColor} px-5 py-3 flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                    <MotivIcon size={16} className="text-white/90" />
                    <span className="text-white text-xs font-semibold">{motivMsg}</span>
                </div>
                {totalDue > 0 && (
                    <span className="text-white/90 text-xs font-bold">{formatCurrency(totalDue, currency)} expected</span>
                )}
            </div>

            <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <CalendarClock className="text-blue-600" size={20} />
                        <h2 className="text-base font-semibold text-gray-800">Upcoming Payments</h2>
                    </div>
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="text-xs font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded border-0 focus:ring-0 cursor-pointer"
                    >
                        <option value="all">Next 60 Days</option>
                        <option value="this_week">This Week</option>
                        <option value="next_week">Next Week</option>
                        <option value="this_month">This Month</option>
                        <option value="next_month">Next Month</option>
                    </select>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-100 mb-4">
                    {(['client', 'developer'] as const).map(tab => {
                        const count = tab === 'client' ? clientPayments.length : devPayments.length;
                        const hasOverdue = (tab === 'client' ? clientPayments : devPayments).some(p => isOverdue(p.dueDate, p.status));
                        return (
                            <button
                                key={tab}
                                className={`pb-2 px-4 text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === tab ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                onClick={() => setActiveTab(tab)}
                            >
                                {tab === 'client' ? 'Client' : 'Developer'}
                                <span className={`py-0.5 px-1.5 rounded-full text-xs ${hasOverdue ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
                            </button>
                        );
                    })}
                </div>

                {displayPayments.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-300 mb-2" />
                        <p className="text-sm font-medium text-gray-500">All clear!</p>
                        <p className="text-xs text-gray-400 mt-0.5">No upcoming {activeTab === 'client' ? 'client payments' : 'developer payouts'}.</p>
                    </div>
                ) : (
                    <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1 custom-scrollbar">
                        {displayPayments.map((payment) => {
                            const dueSoon = isDueSoon(payment.dueDate);
                            const overdue = isOverdue(payment.dueDate, payment.status);
                            const daysBadge = getDaysLabel(payment.dueDate, payment.status);

                            return (
                                <div
                                    key={payment.id}
                                    className={`relative flex items-center justify-between p-3 rounded-xl border transition-all ${
                                        overdue ? 'bg-red-50 border-red-200' : dueSoon ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
                                    }`}
                                >
                                    {/* Left accent bar */}
                                    <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${overdue ? 'bg-red-400' : dueSoon ? 'bg-amber-400' : 'bg-blue-300'}`} />

                                    <div className="flex items-center gap-3 ml-2 min-w-0">
                                        <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${overdue ? 'bg-red-100' : dueSoon ? 'bg-amber-100' : 'bg-blue-50'}`}>
                                            {payment.type === 'Project Deadline' ? (
                                                <Flag size={15} className={overdue ? 'text-red-500' : dueSoon ? 'text-amber-500' : 'text-purple-500'} />
                                            ) : (payment.type === 'Software' || payment.type === 'Hosting') ? (
                                                <RefreshCw size={15} className={overdue ? 'text-red-500' : dueSoon ? 'text-amber-500' : 'text-blue-500'} />
                                            ) : overdue ? (
                                                <AlertCircle size={15} className="text-red-500" />
                                            ) : (
                                                <CalendarClock size={15} className={dueSoon ? 'text-amber-500' : 'text-blue-500'} />
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-gray-800 truncate">{payment.title}</p>
                                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                <span className="text-xs text-gray-400">{payment.type}</span>
                                                {payment.startDate && (
                                                    <>
                                                        <span className="text-xs text-gray-300">·</span>
                                                        <span className="text-xs text-gray-400">Start: {new Date(payment.startDate).toLocaleDateString()}</span>
                                                    </>
                                                )}
                                                <span className="text-xs text-gray-300">·</span>
                                                <span className={`text-xs font-medium ${overdue ? 'text-red-600' : dueSoon ? 'text-amber-600' : 'text-gray-500'}`}>
                                                    {payment.type === 'Project Deadline' ? 'End: ' : ''}{payment.dueDate ? new Date(payment.dueDate).toLocaleDateString() : 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="shrink-0 text-right flex flex-col items-end gap-1 ml-2">
                                        {daysBadge && (
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${daysBadge.color}`}>{daysBadge.label}</span>
                                        )}
                                        {payment.type !== 'Project Deadline' && payment.amount > 0 && (
                                            <p className="text-sm font-bold text-gray-800">{formatCurrency(payment.amount, payment.currency || currency)}</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UpcomingPayments;
